<template>
  <modal v-if="isOpen" @leave="close()" show-close-btn>
    <loader v-if="media === null || savingFolder || deletingMedia" />
    <div v-else>
      <div class="flex mb-6">
        <h2 class="text-lg flex-grow">Media Manager - {{ media.name || 'Home' }}</h2>
        <button v-if="showNewFolder || showUploader || mediaToDelete || showDeleteFolder || renameFolderForm" class="bg-grey-lighter hover:bg-grey-light text-black p-3 text-center rounded ml-3" @click="closeNewFolder(); closeUpload(); closeDeleteMedia(); closeDeleteFolder(); closeRenameFolder()">
          <i class="fas fa-arrow-left fa-fw"></i> Back
        </button>
        <div v-else class="flex">
          <button class="bg-grey-lighter hover:bg-grey-light text-black p-3 text-center rounded ml-3" v-if="media && media.parent" title="Delete Folder" @click="showDeleteFolder = true">
            <i class="fas fa-trash-alt fa-fw"></i>
          </button>
          <button class="bg-grey-lighter hover:bg-grey-light text-black p-3 text-center rounded ml-3" v-if="media && media.parent" title="Rename Folder" @click="showRenameFolder">
            <i class="fas fa-pencil-alt fa-fw"></i>
          </button>
          <button class="bg-grey-lighter hover:bg-grey-light text-black p-3 text-center rounded ml-3" title="Create Folder" @click="showNewFolder = true">
            <i class="fas fa-folder-plus fa-fw"></i>
          </button>
          <button class="bg-green-dark hover:bg-green text-white p-3 text-center rounded ml-3" @click="showUploader = true">
            <i class="fas fa-cloud-upload-alt fa-fw"></i> Upload Images
          </button>
        </div>
      </div>
      <div v-if="mediaToDelete">
        <p class="mb-6">Are you sure you want to delete {{ mediaToDelete.filename }}? The file will display as a broken image in any published e-zines until it is replaced.</p>

        <button class="bg-transparent hover:bg-tertiary active:bg-primary text-primary hover:text-white py-2 px-6 text-sm border border-tertiary hover:border-transparent rounded shadow" @click="deleteMedia()">
          Yes, delete
        </button>

        <button class="bg-transparent hover:bg-tertiary active:bg-primary text-primary hover:text-white py-2 px-6 text-sm border border-tertiary hover:border-transparent rounded shadow ml-3" @click="closeDeleteMedia()">
          Cancel
        </button>
      </div>
      <div v-else-if="showUploader">
        <upload-dropzone accept="image/*" @change="queueFiles($event)" />

        <div v-if="newFiles.length > 0" class="mt-6">
          <table class="w-full mb-6">
            <tbody>
              <tr v-for="file in newFiles" :key="file.file.name">
                <td class="py-2">{{ file.file.name }}</td>
                <td class="py-2 text-right">
                  <span v-if="file.error" class="text-red-dark text-sm">
                    <i class="fas fa-exclamation-triangle"></i> {{ file.error }}
                  </span>
                  <i class="fas fa-check" v-else-if="file.complete"></i>
                  <span v-else-if="uploading">{{ file.progress }}%</span>
                </td>
              </tr>
            </tbody>
          </table>

          <button :disabled="uploading" class="bg-transparent hover:bg-tertiary active:bg-primary text-primary hover:text-white py-2 px-6 text-sm border border-tertiary hover:border-transparent rounded shadow" @click="uploadFiles()">
            <loader v-if="uploading" />
            <span v-else><i class="fas fa-cloud-upload-alt"></i> Upload Files</span>
          </button>

          <button class="bg-transparent hover:bg-tertiary active:bg-primary text-primary hover:text-white py-2 px-6 text-sm border border-tertiary hover:border-transparent rounded shadow ml-3" @click="closeUpload()">
            View Uploads / Close
          </button>
        </div>
      </div>
      <div v-else-if="renameFolderForm">
        <div class="mb-6">
          <label class="block mb-2">New Folder Name</label>
          <input type="text" v-model="renameFolderForm.name" class="border px-3 py-2 w-full" />
          <div class="mt-2 text-red-dark text-sm" v-if="errorBag.get('name')">
            <i class="fas fa-exclamation-triangle"></i> {{ errorBag.get('name') }}
          </div>
        </div>

        <div class="flex justify-end">
          <button class="bg-transparent hover:bg-tertiary active:bg-primary text-primary hover:text-white py-2 px-6 text-sm border border-tertiary hover:border-transparent rounded shadow" @click="renameFolder()">
            Rename Folder
          </button>

          <button class="bg-transparent hover:bg-tertiary active:bg-primary text-primary hover:text-white py-2 px-6 text-sm border border-tertiary hover:border-transparent rounded shadow ml-3" @click="closeRenameFolder()">
            Cancel
          </button>
        </div>
      </div>
      <div v-else-if="showNewFolder">
        <div class="mb-6">
          <label class="block mb-2">New Folder Name</label>
          <input type="text" v-model="newFolderForm.name" class="border px-3 py-2 w-full" />
          <div class="mt-2 text-red-dark text-sm" v-if="errorBag.get('name')">
            <i class="fas fa-exclamation-triangle"></i> {{ errorBag.get('name') }}
          </div>
        </div>

        <button class="bg-transparent hover:bg-tertiary active:bg-primary text-primary hover:text-white py-2 px-6 text-sm border border-tertiary hover:border-transparent rounded shadow" @click="createFolder()">
          Create Folder
        </button>

        <button class="bg-transparent hover:bg-tertiary active:bg-primary text-primary hover:text-white py-2 px-6 text-sm border border-tertiary hover:border-transparent rounded shadow ml-3" @click="closeNewFolder()">
          Cancel
        </button>
      </div>
      <div v-else-if="showDeleteFolder">
        <p class="mb-6">Are you sure you want to delete this folder and all of its contents? This cannot be undone.</p>

        <div class="flex justify-end">
          <button class="bg-transparent hover:bg-tertiary active:bg-primary text-primary hover:text-white py-2 px-6 text-sm border border-tertiary hover:border-transparent rounded shadow" @click="deleteFolder()">
            Delete Folder
          </button>

          <button class="bg-transparent hover:bg-tertiary active:bg-primary text-primary hover:text-white py-2 px-6 text-sm border border-tertiary hover:border-transparent rounded shadow ml-3" @click="closeDeleteFolder()">
            Cancel
          </button>
        </div>
      </div>
      <div v-else>
        <div class="flex flex-wrap -mx-2 mb-6">
          <div v-if="media.parent" class="w-full md:w-1/2 lg:w-1/3 px-2 mb-3">
            <button class="bg-grey-lighter hover:bg-grey-light text-black p-3 text-center rounded block w-full" @click="selectedFolderId = media.parent.id">
              <i class="fas fa-arrow-left"></i> {{ media.parent.name || 'Home' }}
            </button>
          </div>

          <div v-for="child in media.children" :key="child.id" class="w-full md:w-1/2 lg:w-1/3 px-2 mb-3">
            <button class="bg-grey-lighter hover:bg-grey-light text-black p-3 text-center rounded block w-full" @click="selectedFolderId = child.id">
              <i class="fas fa-folder"></i> {{ child.name }}
            </button>
          </div>
        </div>

        <div class="flex flex-wrap -mx-2">
          <div class="w-full md:w-1/2 lg:w-1/3 px-2 mb-6" v-for="m in media.media" :key="m.id">
            <div class="border-2 rounded overflow-hidden h-full flex flex-col">
              <img :src="m.large_url" class="w-full object-contain bg-grey-light h-48" loading="lazy" />
              <div class="p-3 flex flex-col flex-grow">
                <div class="flex-grow">
                  <strong class="block truncate" :title="m.filename">{{ m.filename }}</strong>
                  <span class="text-grey text-xs">{{ m.filesize | sizeInMb }}MB | {{ m.created_at | datetime }}</span>
                </div>
                <div class="flex mt-3">
                  <button @click="selectImage(m)" class="bg-grey-light hover:bg-grey rounded block p-2 w-full text-sm">
                    Select Image
                  </button>

                  <button @click="mediaToDelete = m" class="bg-grey-light hover:bg-grey rounded block p-2 ml-3" title="Delete Image">
                    <i class="fas fa-trash-alt fa-fw"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </modal>
</template>

<script>
import { v2Api } from '@/http'
import ErrorBag from '@/ErrorBag'
import UploadDropzone from '@/components/UploadDropzone'
import { filter } from 'lodash'

export default {
  components: {
    UploadDropzone
  },
  props: {
    assignableType: {
      type: String,
      default: 'client'
    },
    assignableId: {
      type: Number,
      required: true
    },
    selectedFile: {
      type: String,
      default: null
    },
    imageSize: {
      type: String,
      default: 'hd'
    }
  },
  data () {
    return {
      isOpen: false,
      media: null,
      selectedFolderId: null,
      showNewFolder: false,
      newFolderForm: {
        name: null
      },
      errorBag: new ErrorBag(),
      savingFolder: false,
      showUploader: false,
      showDeleteFolder: false,
      renameFolderForm: null,
      newFiles: [],
      uploading: false,
      mediaToDelete: null,
      deletingMedia: false,
      deletingFolder: false,
      renamingFolder: false
    }
  },
  methods: {
    open () {
      this.isOpen = true
      this.$emit('opened')
      if (this.media === null) {
        this.fetchMedia()
      }
    },
    close () {
      this.isOpen = false
      this.$emit('closed')
    },
    fetchMedia () {
      this.media = null

      const path = this.selectedFolderId ? `ezine-media-folders/${this.selectedFolderId}` : 'ezine-media-folders'
      const params = this.selectedFolderId ? {} : this.assignableType === 'network' ? {
        network_id: this.assignableId
      } : {
        client_id: this.assignableId
      }

      v2Api.get(path, { params }).then((response) => {
        this.media = response.data

        if (this.selectedFolderId === null) {
          this.selectedFolderId = response.data.id
        }
      })
    },
    createFolder () {
      this.savingFolder = true
      this.errorBag.clear()

      v2Api.post('ezine-media-folders', {
        parent_id: this.selectedFolderId,
        ...this.newFolderForm
      }).then((response) => {
        this.selectedFolderId = response.data.id
        this.closeNewFolder()
      }, (errors) => {
        this.savingFolder = false
        this.errorBag.store(errors.response.data)
      })
    },
    closeNewFolder () {
      this.savingFolder = false
      this.showNewFolder = false
      this.errorBag.clear()
      this.newFolderForm.name = null
    },
    closeUpload () {
      this.uploading = false
      this.showUploader = false
      this.errorBag.clear()
      this.newFiles = []
    },
    queueFiles (files) {
      for (let i = 0; i < files.length; i++) {
        this.newFiles.push({
          progress: 0,
          complete: false,
          error: null,
          file: files[i]
        })
      }
    },
    uploadFiles () {
      this.newFiles = filter(this.newFiles, (file) => !file.error && !file.complete)
      if (this.newFiles.length === 0) {
        return
      }
      this.uploading = true

      for (let i = 0; i < this.newFiles.length; i++) {
        let formData = new FormData()
        formData.append('ezine_media_folder_id', this.selectedFolderId)
        formData.append('file', this.newFiles[i].file)

        v2Api.post('ezine-media', formData, {
          onUploadProgress: (e) => {
            this.newFiles[i].progress = Math.ceil((100 / e.total) * e.loaded)
          }
        }).then((response) => {
          this.newFiles[i].complete = true
          this.media.media.unshift(response.data)

          const complete = filter(this.newFiles, (file) => file.complete).length
          const total = this.newFiles.length

          if (complete === total) {
            this.uploading = false
          }
        }, (errors) => {
          this.newFiles[i].completed = true
          this.newFiles[i].error = errors.response.data.errors.file.join(' ')
        })
      }
    },
    selectImage (media) {
      this.$emit('selected', this.imageSize === 'large' ? media.large_url : media.hd_url)
      this.close()
    },
    deleteMedia () {
      this.deletingMedia = true

      v2Api.delete(`ezine-media/${this.mediaToDelete.id}`).then((response) => {
        this.media.media.splice(this.media.media.indexOf(this.mediaToDelete), 1)
        this.closeDeleteMedia()
      })
    },
    deleteFolder () {
      this.deletingFolder = true

      v2Api.delete(`ezine-media-folders/${this.selectedFolderId}`).then(() => {
        this.selectedFolderId = this.media.parent.id
        this.media = null
        this.closeDeleteFolder()
      })
    },
    closeDeleteMedia () {
      this.mediaToDelete = null
      this.deletingMedia = false
    },
    closeDeleteFolder () {
      this.showDeleteFolder = false
      this.deletingFolder = false
    },
    showRenameFolder () {
      this.renameFolderForm = {
        name: null
      }
      this.renamingFolder = false
    },
    closeRenameFolder () {
      this.renameFolderForm = null
      this.renamingFolder = false
    },
    renameFolder () {
      this.renamingFolder = true
      v2Api.patch(`ezine-media-folders/${this.selectedFolderId}`, this.renameFolderForm).then(() => {
        this.media = null
        this.fetchMedia()
        this.closeRenameFolder()
      }, (errors) => {
        this.errorBag.store(errors.response.data)
        this.renamingFolder = false
      })
    }
  },
  watch: {
    assignableType () {
      this.selectedFolderId = null
    },
    assignableId () {
      this.selectedFolderId = null
    },
    selectedFolderId (newValue, oldValue) {
      if (oldValue === null) {
        return
      }

      this.fetchMedia()
    }
  }
}
</script>
